import validateConfig from '/business/shared/functions/validateConfig';
import { contexts, locales } from '/shared/util/Statics';
import type { Config } from '/index';
import type { DecoratedQuestionTreeItem, QuestionTreeItem } from '/business/shared/functions/types';

import testLogo from '/business/airbp/images/airbp-logo.png';
import otherFuelsEducation from '/business/airbp/components/questions/education/other-fuels.json';
import electricityEducation from '/business/airbp/components/questions/education/electricity.json';

// QUESTIONS
import { getQuestionsLookup } from '/business/airbp/components/questions/questions';
import decorateQuestions from '/business/shared/functions/decorateQuestions';
import flattenQuestions from '/business/shared/functions/flattenQuestions';

// IMAGES EN
import ExampleReductionDocumentEN from '/business/shared/images/locale/en/example-reduction-document.png';

// IMAGES XX
import ExampleReductionDocumentXX from '/business/shared/images/locale/xx/example-reduction-document.png';

import ExampleReductionDocumentZH from '/business/shared/images/locale/zh/example-reduction-document.png';

// INFOCARDS
import {
  barGraphLayoutIsVerticallyScalable,
  infoCardsV3,
  introCards,
  reductionCardsV3,
  reductionCardsSlides,
  reductionInstructions,
} from '/business/airbp/reducePage';

import { landingPageIntroSteps } from '/business/airbp/landingPage';

type ConfigQuestionMethods = Pick<Config, 'getDecoratedQuestions' | 'getFlattenedQuestions' | 'getQuestionsLookup'>;
type WhitelabelConfig = Omit<Config, keyof ConfigQuestionMethods> &
  Partial<ConfigQuestionMethods> & {
    defaultAspirationReductionPercent?: number;
    hasAspirationalOffsetTarget?: boolean;
  };
type FlattenedQuestionCache = Partial<Record<'deep' | 'shallow', QuestionTreeItem[]>>;

const getConfiguredQuestionsLookup = (): QuestionTreeItem[] => {
  const questionsLookup = config.getQuestionsLookup;
  if (!questionsLookup) {
    throw new Error('Questions lookup is not configured');
  }
  return questionsLookup();
};

const getConfiguredDecoratedQuestions = (): DecoratedQuestionTreeItem[] => {
  const decoratedQuestions = config.getDecoratedQuestions;
  if (!decoratedQuestions) {
    throw new Error('Decorated questions are not configured');
  }
  return decoratedQuestions();
};

const config: WhitelabelConfig = {
  app: contexts.AIRBP,
  name: 'airbp',
  transKey: '/airbp',
  findOutMoreLink: 'https://www.castrol.com/360dealer',
  findOutMoreLinkLandingPage: 'https://www.bp.com/en_gb/carbon-connect/home/our-carbon-credit-portfolio.html',
  isAppDisabled: false,

  emissionTypes: {
    // needs to be commented out if there are less questions than in castrol version
    electricity: 'electricity',
    refrigerants: 'refrigerants',
    otherFuels: 'other',
    companyVehicles: 'company-vehicles',
    businessTravel: 'business-travel',
    commutes: 'employee-commutes',
    fireFightingExercises: 'fire-fighting-exercises',
    onSiteWaterTreatment: 'on-site-water-treatment',
    onSitePowerGeneration: 'on-site-power-generation',
    boilersAndFunraces: 'boilers-and-furnaces',
    electricityLighting: 'electricity-lighting',
    electricityHeating: 'electricity-heating',
    elctricityCooling: 'electricity-cooling',
    electricityOther: 'electricity-other',
    evCharging: 'ev-charging',
    // electricitySummary: 'electricity-summary',
  },
  scopeKeys: {
    'scope-1': 'Scope 1',
    'scope-2': 'Scope 2',
    'scope-3': 'Scope 3',
  },
  emissionFields: {
    location: {
      hasOEM: false,
    },
    electricity: {
      hasSourceBreakdown: true,
    },
    companyVehiclesTotalFuelConsumption: {
      hasEvCharging: false,
    },
  },
  reducePage: {
    barGraphLayoutIsVerticallyScalable,
    hasUpdatedEmissionsTargetIntro: true,
    introCards,
    infoCardsV3,
    reductionCardsV3,
    reductionCardsSlides,
    reductionInstructions,
    hasSubtitle: true,
    hasShortenedCarbonExplainer: true,
    baselineHasUpdatedDesign: true,
    reductionsConfig: {
      showEmptyScopes: false,
      isMinimumReduction: false,
      minimumReductionPercentage: null,
      hasAspirationalOffsetTarget: true,
      cumulativeReductionPercentage: 7,
      defaultNumberOfPeriods: 3,
      periodReductions: {
        // period1: 0,
        // period2: 12,
        // period3: 24,
        // period4: 36,
      },
    },
  },
  dashboard: {
    hasDropdownOnLevel: true,
  },
  createApp: {
    hasEmailExplainer: true,
  },
  createAppSuccess: {
    hasDisclaimer: false,
    hasTrees: false,
  },
  landingPage: {
    hasWelcomePageFooter: false,
    steps: landingPageIntroSteps,
    hasFinePrint: false,
    sidebarVerticalOffset: '10.5rem',
    hasTrees: false,
  },
  questionsPage: {
    hasCustomValidationValues: false,
    hasGroupSubtitle: true,
    hasLocationBulkUpload: false,
    location: {
      hasInstructions: false,
    },
    education: { otherFuelsEducation, electricityEducation },
  },
  resultsPage: {
    hasTotal: false,
    donut: true,
  },
  summaryPage: {
    hasIcons: true,
  },
  paymentPage: {
    hasExtraFieldNote: true,
  },
  reportingPeriods: {
    month: 12,
    reportingEndDateWithinPastXMonths: null,
  },
  settings: {
    allowUserGuideDownloads: true,
    allowOwnerDownloadUserGuide: false,
    hasLongReportingPeriodModal: true,
  },
  defaultVariables: [
    {
      locale: locales.ENGLISH,
      varWordApplication: 'Carbon review',
      varWordBpBusiness: 'Air bp',
      varWordCustomerType: 'Airport',
      varWordApplicationPlural: 'Carbon reviews',
      varWordCustomerTypePlural: 'Airports',
      varWordApplicationLower: 'carbon review',
      varWordBpBusinessLower: 'air bp',
      varWordCustomerTypeLower: 'airport',
      varWordApplicationLowerPlural: 'carbon reviews',
      varWordCustomerTypeLowerPlural: 'airports',
    },
    {
      locale: locales.TEST,
      varWordApplication: 'Carbon review',
      varWordBpBusiness: 'Air bp',
      varWordCustomerType: 'Airport',
      varWordApplicationPlural: 'Carbon reviews',
      varWordCustomerTypePlural: 'Airports',
      varWordApplicationLower: 'carbon review',
      varWordBpBusinessLower: 'air bp',
      varWordCustomerTypeLower: 'airport',
      varWordApplicationLowerPlural: 'carbon reviews',
      varWordCustomerTypeLowerPlural: 'airports',
    },
    {
      locale: locales.CHINESE,
      varWordApplication: 'Carbon review',
      varWordBpBusiness: 'Air bp',
      varWordCustomerType: 'Airport',
      varWordApplicationPlural: 'Carbon reviews',
      varWordCustomerTypePlural: 'Airports',
      varWordApplicationLower: 'carbon review',
      varWordBpBusinessLower: 'air bp',
      varWordCustomerTypeLower: 'airport',
      varWordApplicationLowerPlural: 'carbon reviews',
      varWordCustomerTypeLowerPlural: 'airports',
    },
  ],
  locales: [locales.ENGLISH, locales.TEST],
  images: {
    logoImage: testLogo,
    logoImageMaxHeight: '101px',
    locales: {
      en: {
        exampleReductionDoc: ExampleReductionDocumentEN,
      },
      xx: {
        exampleReductionDoc: ExampleReductionDocumentXX,
      },
      zh: {
        exampleReductionDoc: ExampleReductionDocumentZH,
      },
    },
  },
  components: {
    applicationSettings: {
      DetailsDescription: import('/business/airbp/components/pages/application-settings/DetailsDescription'),
    },
    ReportingPeriodDescription: import(
      '/business/airbp/components/pages/application-settings/ReportingPeriodDescription'
    ),
    TermsAndConditions: import('/business/airbp/components/pages/landing/TermsAndConditions'),

    createApplication: {
      CreateApplicationCountryOfOperationSub: import(
        '/business/airbp/components/pages/create-application/CreateApplicationCountryOfOperationSub'
      ),
    },
  },
  documents: {
    en: { methodologyStatement: null, termsAndConditions: null },
    xx: { methodologyStatement: null, termsAndConditions: null },
    zh: { methodologyStatement: null, termsAndConditions: null },
  },
  reports: {
    // get reports in here
  },

  // TODO: [WR] find a proper solution to hide the contribution widget on create application page
  isDefaultContributionValue: true,
  hasContribution: false,
  step1Answers: {
    minimumAnswersRequired: 1,
    allAnswersRequired: false,
  },
  hasAspirationalOffsetTarget: true,
  defaultAspirationReductionPercent: 5,
  // TODO: [PG] we might want to use it for scope-based builds
  hasScopeBasedEmissions: true,
  userFields: {
    hasSigningRoles: false,
    hasApproverRole: false,
    hasReviewerRole: false,
  },
};

validateConfig(config);

config.getQuestionsLookup = (() => {
  let cached: QuestionTreeItem[] | null = null;
  return () => {
    if (!cached) {
      cached = getQuestionsLookup();
    }
    return cached;
  };
})();

config.getDecoratedQuestions = (() => {
  let cached: DecoratedQuestionTreeItem[] | null = null;
  return () => {
    if (!cached) {
      cached = decorateQuestions(getConfiguredQuestionsLookup());
    }
    return cached;
  };
})();

config.getFlattenedQuestions = (() => {
  const cache: FlattenedQuestionCache = {};
  return (deep = false) => {
    const key = deep ? 'deep' : 'shallow';
    if (!cache[key]) {
      cache[key] = flattenQuestions(getConfiguredDecoratedQuestions(), deep);
    }
    return cache[key] ?? [];
  };
})();

export default config;
